import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen } from "lucide-react";

import { fetchModulePersonalNotesApi } from "../../../services/modulePersonalNotesApi";
import ModulePersonalNotesFilters from "./ModulePersonalNotesFilters";
import ModulePersonalNotesList from "./ModulePersonalNotesList";
import ModulePersonalNotesSearch from "./ModulePersonalNotesSearch";
import "./ModulePersonalNotesPage.css";

const extractErrorMessage = (error) =>
    error?.response?.data?.message || error?.response?.data?.error || error?.message || "Chargement impossible.";

export default function ModulePersonalNotesPage() {
    const { moduleId } = useParams();
    const navigate = useNavigate();
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        query: "",
        contentType: "",
        sectionId: "",
        sort: "RECENT",
    });

    const loadNotes = useCallback(async () => {
        if (!moduleId) return;
        setLoading(true);
        setError(null);
        try {
            const response = await fetchModulePersonalNotesApi(moduleId, filters);
            setNotes(response.data || []);
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [filters, moduleId]);

    useEffect(() => {
        const timer = window.setTimeout(loadNotes, 200);
        return () => window.clearTimeout(timer);
    }, [loadNotes]);

    const sections = useMemo(() => {
        const byId = new Map();
        notes.forEach((note) => {
            if (note.sectionId && !byId.has(String(note.sectionId))) {
                byId.set(String(note.sectionId), { id: note.sectionId, title: note.sectionTitle || `Section ${note.sectionId}` });
            }
        });
        return Array.from(byId.values());
    }, [notes]);

    const openNote = (note) => {
        navigate(`/formation/apprenant/modules/${moduleId}/player`, {
            state: { personalNoteNavigation: note },
        });
    };

    return (
        <div className="mpn-page">
            <header className="mpn-header">
                <button type="button" className="mpn-back" onClick={() => navigate(`/formation/apprenant/modules/${moduleId}/player`)}>
                    <ArrowLeft size={16} />
                    Retour au module
                </button>
                <div>
                    <span>Notes personnelles</span>
                    <h1>{notes[0]?.moduleTitle || "Mes notes du module"}</h1>
                    <p>Retrouvez vos notes privees et revenez directement au bon moment du contenu.</p>
                </div>
            </header>

            <section className="mpn-toolbar">
                <ModulePersonalNotesSearch value={filters.query} onChange={(query) => setFilters((current) => ({ ...current, query }))} />
                <ModulePersonalNotesFilters filters={filters} sections={sections} onChange={setFilters} />
            </section>

            {error ? <div className="mpn-state mpn-state--error">{error}</div> : null}
            {loading ? <div className="mpn-state">Chargement des notes...</div> : null}
            {!loading && !error && notes.length === 0 ? (
                <div className="mpn-state">
                    <BookOpen size={18} />
                    Aucune note personnelle dans ce module.
                </div>
            ) : null}
            {!loading && notes.length > 0 ? <ModulePersonalNotesList notes={notes} onOpen={openNote} /> : null}
        </div>
    );
}
